import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, animate, motion, useIsPresent, useMotionValue, usePresence, useReducedMotion, useTransform } from "motion/react";
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./toast-stack.module.css";
/** Each tucked toast shows this many pixels above the one in front of it. */
const PEEK = 14;
/** Space between toasts when the stack is open. */
const GAP = 12;
/** Tucked toasts shrink by this much per step back. */
const STEP = .05;
/** The card's top and bottom borders, added to the measured content height. */
const BORDER = 2;
/** A swipe this far right has faded most of the way out. */
const SWIPE_FADE = 280;
const EMPTY = [];
const typeLabels = { success: "Success", info: "Info", warning: "Warning", error: "Error", loading: "In progress" };
const icons = { success: CircleCheck, info: Info, warning: TriangleAlert, error: CircleX };
const standard = [...motionTokens.ease.standard];
const enterEase = [...motionTokens.ease.enter];
const fade = { duration: motionTokens.duration.fast, ease: standard };
const enterFade = { duration: motionTokens.duration.standard, ease: enterEase };
const reducedFade = { duration: motionTokens.duration.fast, ease: standard };
const exitFast = { duration: motionTokens.duration.fast, ease: standard };
const textIn = { opacity: 0, y: "0.3em", filter: `blur(${motionTokens.blur.soft}px)` };
const textOut = { opacity: 0, y: "-0.3em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: exitFast };
const iconIn = { opacity: 0, scale: .6, filter: `blur(${motionTokens.blur.subtle}px)` };
const shown = { opacity: 1, y: "0em", scale: 1, filter: "blur(0px)" };
const fadeOnly = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0, transition: reducedFade }, transition: reducedFade };
const textSwap = { initial: textIn, animate: shown, exit: textOut, transition: { duration: motionTokens.duration.standard, ease: enterEase } };
/** Scale rides the spring; opacity and blur tween so the blur never overshoots below zero. */
const iconSwap = { initial: iconIn, animate: shown, exit: { ...iconIn, transition: exitFast }, transition: { ...motionTokens.spring.snappy, opacity: fade, filter: fade } };
const durationFor = (type, base) => type === "loading" ? Infinity : type === "warning" || type === "error" ? base * 1.6 : base;
/** Where a release at this velocity would come to rest, using a fast scroll deceleration. */
const project = (velocity) => velocity / 1000 * .99 / (1 - .99);
/** Resistance past an edge: the pull keeps moving, but less and less, the way a scroll view overscrolls. */
const rubberBand = (distance, dimension) => (1 - 1 / (distance * .55 / dimension + 1)) * dimension;
const subscribeVisibility = (listener) => { document.addEventListener("visibilitychange", listener); return () => document.removeEventListener("visibilitychange", listener); };
const getServerSnapshot = () => EMPTY;
const focusVisible = (element) => { try {
    return element.matches(":focus-visible");
}
catch {
    return true;
} };
/**
 * Closed, tucked toasts take the front toast's height so their edges line up; open, each takes its own and they stack with a gap.
 * The list is the hover target and is sized to cover the gaps, so moving between open toasts never collapses the stack.
 */
function layoutStack(toasts, heights, expanded, visibleToasts) {
    const front = toasts.length ? heights[toasts[0].id] ?? 0 : 0;
    const tops = toasts.reduce((sum, item, index) => [...sum, sum[index] + (index < visibleToasts ? (heights[item.id] ?? 0) + GAP : 0)], [0]);
    const targets = toasts.map((item, index) => {
        const visible = index < visibleToasts;
        const depth = Math.min(index, visibleToasts);
        return expanded
            ? { y: -tops[index], scale: 1, height: heights[item.id] ?? 0, opacity: visible ? 1 : 0, content: 1 }
            : { y: -PEEK * depth, scale: 1 - STEP * depth, height: index === 0 ? heights[item.id] ?? 0 : front, opacity: visible ? 1 : 0, content: index === 0 ? 1 : 0 };
    });
    const listHeight = !toasts.length ? 0 : expanded ? tops[toasts.length] - GAP : front + PEEK * (Math.min(toasts.length, visibleToasts) - 1);
    return { targets, listHeight };
}
function createToastStore(base, limit) {
    let toasts = [];
    let seq = 0;
    const listeners = new Set();
    const commit = (next) => { toasts = next; listeners.forEach(listener => listener()); };
    const update = (id, patch) => {
        const found = toasts.find(item => item.id === id);
        if (!found)
            return;
        const type = patch.type ?? found.type;
        const record = { ...found, ...patch, type, duration: patch.duration ?? durationFor(type, base), version: found.version + 1 };
        commit(toasts.map(item => item.id === id ? record : item));
    };
    const toast = ({ id, ...options }) => {
        if (id && toasts.some(item => item.id === id)) {
            update(id, options);
            return id;
        }
        const type = options.type ?? "info";
        seq += 1;
        const record = { ...options, id: id ?? `toast-${seq}`, type, duration: options.duration ?? durationFor(type, base), seq, version: 0 };
        commit([record, ...toasts].slice(0, limit));
        return record.id;
    };
    const dismiss = id => {
        if (id === undefined) {
            if (toasts.length)
                commit([]);
            return;
        }
        if (toasts.some(item => item.id === id))
            commit(toasts.filter(item => item.id !== id));
    };
    const runAction = (id) => {
        const found = toasts.find(item => item.id === id);
        if (!found?.action)
            return;
        found.action.onClick(id);
        if (toasts.find(item => item.id === id)?.version === found.version)
            dismiss(id);
    };
    return {
        toast, update, dismiss, runAction,
        subscribe: listener => { listeners.add(listener); return () => { listeners.delete(listener); }; },
        getSnapshot: () => toasts,
    };
}
const ToastStackContext = createContext(null);
function useStore() {
    const store = useContext(ToastStackContext);
    if (!store)
        throw new Error("Render toast stack consumers inside <ToastStackProvider>.");
    return store;
}
/** Scopes a toast queue to its subtree, so two stacks on one page never share toasts. */
export function ToastStackProvider({ children, duration = 5000, limit = 12 }) {
    const [store] = useState(() => createToastStore(duration, limit));
    return <ToastStackContext.Provider value={store}>{children}</ToastStackContext.Provider>;
}
/** Shows, updates, and dismisses toasts from anywhere inside the provider. `count` is how many toasts are queued. */
export function useToastStack() {
    const store = useStore();
    const count = useSyncExternalStore(store.subscribe, () => store.getSnapshot().length, () => 0);
    return { toast: store.toast, update: store.update, dismiss: store.dismiss, count };
}
/** Outgoing copies are hidden from assistive tech while they fade, so the live region reads only the current text. */
function Swap(props) {
    const present = useIsPresent();
    return <motion.span {...props} aria-hidden={present ? props["aria-hidden"] : true}/>;
}
function ToastItem({ toast, target, expanded, front, hidden, paused, reduce, store, onMeasure, onDragChange, onTap, onHandOff }) {
    const { id } = toast;
    const [isPresent, safeToRemove] = usePresence();
    const itemRef = useRef(null);
    const cardRef = useRef(null);
    const contentRef = useRef(null);
    const actionRef = useRef(null);
    const closeRef = useRef(null);
    // Stack geometry lives in motion values, so following the stack, a finger, or a timer never re-renders React per frame.
    const y = useMotionValue(0);
    const scale = useMotionValue(1);
    const height = useMotionValue(0);
    const opacity = useMotionValue(0);
    const contentOpacity = useMotionValue(1);
    const x = useMotionValue(0);
    const swipeFade = useTransform(x, value => value > 0 ? 1 - Math.min(value / SWIPE_FADE, 1) * .75 : 1);
    /** The expanded state the last target was applied under; null until the toast has entered. */
    const applied = useRef(null);
    const swiped = useRef(false);
    const remaining = useRef(toast.duration);
    const gesture = useRef(null);
    const suppressClick = useRef(false);
    const pointerType = useRef("mouse");
    // Measure before the first paint, so the toast enters from exactly its own height below the edge.
    useLayoutEffect(() => {
        const node = contentRef.current;
        if (!node)
            return;
        onMeasure(id, node.offsetHeight + BORDER);
        if (typeof ResizeObserver === "undefined")
            return;
        const observer = new ResizeObserver(() => onMeasure(id, node.offsetHeight + BORDER));
        observer.observe(node);
        return () => observer.disconnect();
    }, [id, onMeasure]);
    // Every stack change retargets the running springs from wherever they are, so rapid toasts and hover changes never queue.
    useLayoutEffect(() => {
        if (!isPresent || target.height <= 0)
            return;
        const entering = applied.current === null;
        const morph = !entering && applied.current !== expanded;
        applied.current = expanded;
        if (reduce) {
            y.jump(target.y);
            scale.jump(target.scale);
            height.jump(target.height);
            animate(opacity, target.opacity, reducedFade);
            animate(contentOpacity, target.content, reducedFade);
            return;
        }
        if (entering) {
            y.jump(target.y + target.height);
            scale.jump(target.scale);
            height.jump(target.height);
            contentOpacity.jump(target.content);
            opacity.jump(0);
        }
        const spring = morph ? motionTokens.spring.morph : motionTokens.spring.smooth;
        animate(y, target.y, spring);
        animate(scale, target.scale, spring);
        if (!entering)
            animate(height, target.height, spring);
        animate(opacity, target.opacity, entering ? enterFade : fade);
        animate(contentOpacity, target.content, fade);
    }, [contentOpacity, expanded, height, isPresent, opacity, reduce, scale, target.content, target.height, target.opacity, target.scale, target.y, y]);
    // An update restarts the clock; pausing keeps whatever time is left.
    useEffect(() => { remaining.current = toast.duration; }, [toast.version, toast.duration]);
    useEffect(() => {
        if (!isPresent || paused || !Number.isFinite(remaining.current) || toast.duration <= 0)
            return;
        const started = performance.now();
        const timer = window.setTimeout(() => store.dismiss(id), Math.max(remaining.current, 0));
        return () => { window.clearTimeout(timer); remaining.current -= performance.now() - started; };
    }, [id, isPresent, paused, store, toast.version, toast.duration]);
    // Leaving: hand focus on before the toast turns inert, then fade out quickly. A closed toast sinks toward its edge; a thrown one keeps flying.
    useEffect(() => {
        if (isPresent)
            return;
        const node = itemRef.current;
        if (node) {
            const active = document.activeElement;
            if (active instanceof HTMLElement && node.contains(active))
                onHandOff(node, focusVisible(active));
            node.setAttribute("inert", "");
        }
        const done = () => safeToRemove?.();
        if (reduce) {
            animate(opacity, 0, reducedFade).then(done);
            return;
        }
        if (!swiped.current) {
            animate(y, y.get() + 10, motionTokens.spring.smooth);
            animate(scale, scale.get() * .96, motionTokens.spring.smooth);
        }
        animate(opacity, 0, { duration: swiped.current ? .22 : motionTokens.duration.exit, ease: standard }).then(done);
    }, [isPresent, onHandOff, opacity, reduce, safeToRemove, scale, y]);
    function onPointerDown(event) {
        pointerType.current = event.pointerType;
        suppressClick.current = false;
        if (event.button !== 0 || !isPresent || event.target.closest("button, a"))
            return;
        // Catch a toast that is still springing back, from exactly where it is.
        x.stop();
        gesture.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, origin: x.get(), active: false, samples: [] };
    }
    function onPointerMove(event) {
        const current = gesture.current;
        if (!current || current.pointerId !== event.pointerId)
            return;
        if (!current.active) {
            const dx = event.clientX - current.startX, dy = event.clientY - current.startY;
            if (Math.hypot(dx, dy) < 6)
                return;
            if (Math.abs(dy) > Math.abs(dx)) {
                gesture.current = null;
                return;
            }
            // Start following from here, so crossing the slop never makes the toast jump.
            current.active = true;
            current.startX = event.clientX;
            suppressClick.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget.dataset.dragging = "";
            window.getSelection()?.removeAllRanges();
            onDragChange(true);
        }
        const raw = current.origin + event.clientX - current.startX;
        // Right follows the finger 1:1; left is the wrong way, so it resists like an overscroll.
        x.set(raw >= 0 ? raw : -rubberBand(-raw, event.currentTarget.offsetWidth));
        current.samples.push({ t: event.timeStamp, x: raw });
        while (current.samples.length > 2 && event.timeStamp - current.samples[0].t > 100)
            current.samples.shift();
    }
    function onPointerEnd(event, cancelled) {
        const current = gesture.current;
        if (!current || current.pointerId !== event.pointerId)
            return;
        gesture.current = null;
        if (!current.active)
            return;
        delete event.currentTarget.dataset.dragging;
        onDragChange(false);
        const first = current.samples[0], last = current.samples[current.samples.length - 1];
        // A finger that stopped before lifting carries no throw.
        const velocity = cancelled || !first || !last || last.t === first.t || event.timeStamp - last.t > 60 ? 0 : (last.x - first.x) / (last.t - first.t) * 1000;
        const width = event.currentTarget.offsetWidth;
        const offset = x.get();
        if (!cancelled && offset > 0 && velocity > -200 && offset + project(velocity) > width * .4) {
            swiped.current = true;
            if (!reduce)
                animate(x, width + 40, { type: "spring", visualDuration: .32, bounce: 0, velocity: Math.max(velocity, 0) });
            store.dismiss(id);
            return;
        }
        if (reduce)
            x.jump(0);
        else
            animate(x, 0, { ...motionTokens.spring.snappy, velocity: offset < 0 ? velocity * .3 : velocity });
    }
    function onCardClick(event) {
        if (suppressClick.current) {
            suppressClick.current = false;
            return;
        }
        // Touch has no hover, so a tap on the card opens or closes the stack instead.
        if (pointerType.current !== "mouse" && !event.target.closest("button, a"))
            onTap();
    }
    // An action that updates its toast usually folds itself away; keyboard focus lands on the close button instead of the page.
    function runAction(event) {
        const keyboard = focusVisible(event.currentTarget);
        store.runAction(id);
        if (keyboard && !store.getSnapshot().find(item => item.id === id)?.action)
            closeRef.current?.focus();
    }
    function onKeyDown(event) {
        if (event.key !== "Escape")
            return;
        event.stopPropagation();
        store.dismiss(id);
    }
    const markMoving = (moving) => { if (actionRef.current)
        actionRef.current.dataset.moving = moving ? "true" : "false"; };
    const Icon = toast.type === "loading" ? null : icons[toast.type];
    const swap = reduce ? fadeOnly : textSwap;
    return <motion.li ref={itemRef} className={styles.item} style={{ y, scale, height, opacity, zIndex: toast.seq }} data-front={front} data-expanded={expanded} inert={hidden} onKeyDown={onKeyDown}>
    <motion.div ref={cardRef} className={styles.card} style={{ x, opacity: swipeFade }} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={event => onPointerEnd(event, false)} onPointerCancel={event => onPointerEnd(event, true)} onClick={onCardClick}>
      <motion.div ref={contentRef} className={styles.content} style={{ opacity: contentOpacity }}>
        {/* A new type morphs its icon in place: the old glyph shrinks into a blur while the new one grows out of it. */}
        <span className={styles.icon} data-type={toast.type} aria-hidden="true">
          <AnimatePresence mode="popLayout" initial={false}>
            <Swap key={toast.type} className={styles.glyph} {...(reduce ? fadeOnly : iconSwap)}>{Icon ? <Icon width={18} height={18} strokeWidth={1.75}/> : <span className={styles.spinner}/>}</Swap>
          </AnimatePresence>
        </span>
        <div className={styles.copy}>
          <span className={styles.srOnly}>{typeLabels[toast.type]}: </span>
          <span className={styles.title}><AnimatePresence mode="popLayout" initial={false}><Swap key={toast.title} className={styles.line} {...swap}>{toast.title}</Swap></AnimatePresence></span>
          <AnimatePresence mode="popLayout" initial={false}>{toast.description ? <Swap key={toast.description} className={styles.description} {...swap}>{toast.description}</Swap> : null}</AnimatePresence>
        </div>
        {/* The action folds its width away when an update removes it, so the copy widens instead of snapping. */}
        <AnimatePresence initial={false}>
          {toast.action ? <motion.div key="action" ref={actionRef} className={styles.actionSlot} initial={reduce ? { opacity: 0 } : { width: 0, opacity: 0 }} animate={{ width: "auto", opacity: 1 }} exit={reduce ? { opacity: 0 } : { width: 0, opacity: 0 }} transition={reduce ? reducedFade : { width: motionTokens.spring.smooth, opacity: fade }} onAnimationStart={() => markMoving(true)} onAnimationComplete={() => markMoving(false)}>
            <button type="button" className={styles.action} onClick={runAction}>{toast.action.label}</button>
          </motion.div> : null}
        </AnimatePresence>
        <button ref={closeRef} type="button" className={styles.close} aria-label="Dismiss notification" onClick={() => store.dismiss(id)}>
          <X width={16} height={16} strokeWidth={1.75} aria-hidden="true"/>
        </button>
      </motion.div>
    </motion.div>
  </motion.li>;
}
export function ToastStack({ label = "Notifications", position = "bottom-right", contained = false, visibleToasts = 3, hotkey = true, className }) {
    const store = useStore();
    const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, getServerSnapshot);
    const reduce = !!useReducedMotion();
    const pageHidden = useSyncExternalStore(subscribeVisibility, () => document.visibilityState === "hidden", () => false);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [tapped, setTapped] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [heights, setHeights] = useState({});
    const regionRef = useRef(null);
    const listRef = useRef(null);
    const returnFocus = useRef(null);
    if (tapped && toasts.length === 0)
        setTapped(false);
    const expanded = toasts.length > 0 && (hovered || focused || tapped);
    const paused = expanded || dragging || pageHidden;
    const measure = useCallback((id, height) => setHeights(current => current[id] === height ? current : { ...current, [id]: height }), []);
    const prune = useCallback(() => setHeights(current => Object.fromEntries(store.getSnapshot().filter(item => item.id in current).map(item => [item.id, current[item.id]]))), [store]);
    const toggleTap = useCallback(() => setTapped(open => !open), []);
    /** Keyboard users land on the next toast; with none left they return to where they were before entering the stack. */
    const handOff = useCallback((leaving, keyboard) => {
        const items = Array.from(listRef.current?.children ?? []);
        const at = items.indexOf(leaving);
        const candidates = items.filter(item => item !== leaving && !item.hasAttribute("inert"));
        const next = candidates.find(item => items.indexOf(item) > at) ?? candidates[candidates.length - 1];
        const button = next?.querySelector("button");
        if (keyboard && button)
            button.focus();
        else if (keyboard && returnFocus.current?.isConnected)
            returnFocus.current.focus();
        else
            document.activeElement?.blur();
    }, []);
    useEffect(() => {
        if (!hotkey)
            return;
        const onKey = (event) => {
            if (!event.altKey || event.metaKey || event.ctrlKey || event.code !== "KeyT")
                return;
            const target = listRef.current?.querySelector(":scope > li:not([inert]) button");
            if (!target)
                return;
            event.preventDefault();
            // Browsers skip the focus ring after a modifier shortcut, so the hotkey asks for it and opens the stack itself.
            target.focus({ focusVisible: true });
            setFocused(true);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [hotkey]);
    useEffect(() => {
        if (!tapped)
            return;
        const onDown = (event) => { if (!regionRef.current?.contains(event.target))
            setTapped(false); };
        document.addEventListener("pointerdown", onDown);
        return () => document.removeEventListener("pointerdown", onDown);
    }, [tapped]);
    const { targets, listHeight } = layoutStack(toasts, heights, expanded, visibleToasts);
    function onFocus(event) {
        const from = event.relatedTarget;
        if (!(from instanceof Node) || !event.currentTarget.contains(from))
            returnFocus.current = from instanceof HTMLElement ? from : null;
        // Only keyboard focus opens the stack; a mouse click on a button should not pin it open.
        if (focusVisible(event.target))
            setFocused(true);
    }
    function onBlur(event) {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget))
            setFocused(false);
    }
    return <section ref={regionRef} className={[styles.viewport, styles[position], contained && styles.contained, className].filter(Boolean).join(" ")} aria-label={hotkey ? `${label} (Alt+T)` : label} aria-live="polite" aria-relevant="additions text" aria-atomic="false" onFocus={onFocus} onBlur={onBlur}>
    <ol ref={listRef} className={styles.list} style={{ height: listHeight }} data-expanded={expanded} onPointerEnter={event => { if (event.pointerType === "mouse")
        setHovered(true); }} onPointerMove={event => { if (event.pointerType === "mouse" && !hovered)
        setHovered(true); }} onPointerLeave={event => { if (event.pointerType === "mouse")
        setHovered(false); }}>
      <AnimatePresence onExitComplete={prune}>
        {toasts.map((item, index) => <ToastItem key={item.id} toast={item} target={targets[index]} expanded={expanded} front={index === 0} hidden={index >= visibleToasts} paused={paused} reduce={reduce} store={store} onMeasure={measure} onDragChange={setDragging} onTap={toggleTap} onHandOff={handOff}/>)}
      </AnimatePresence>
    </ol>
  </section>;
}
export default ToastStack;
